import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ReactComponent as BankImage } from '../../assets/summary_icon_bank.svg';
import { ReactComponent as CardImage } from '../../assets/summary_icon_credit_card.svg';
import Link from '../Link';
import { colors, fontFamilies } from '../baseStyle/Variables';
import { SegmentProps } from '../../state-machine/actions/segment';
import { Application } from '../../gql/graphql';
import loansService from '../../services/loan.service';
import { useTranslation, Trans } from 'react-i18next';

const PaymentMethodStyle = styled.div`
  color: ${colors.darkBlue};
`;
const ImageStyle = styled.div`
  width: 2em;
  margin-right: 10px;
  margin-bottom: 5px;
  max-width: 23px;
`;

const PaymentHeader = styled.div`
  font-family: ${fontFamilies.gilroyBold};
  font-size: 1.2rem;
  margin: 1rem 0;
`;

const RowWrapper = styled.div`
  padding: 0.5rem 0;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  border-bottom: 0.5px solid ${colors.darkBlue};
  border-top: 0.5px solid ${colors.darkBlue};
  margin: 0.5rem 0;
`;

const Label = styled.div`
  font-size: 0.9rem;
  width: 40%;
`;

const Value = styled.div`
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  margin-top: 0.25rem;
  margin-left: auto;
  font-family: ${fontFamilies.interBold};
  width: 60%;
  display: flex;
  text-align: right;
  div:last-child {
    // margin-left: auto;
    // width: 70%;
    display: flex;
  }
`;

const StyledLink = styled.div`
  .a {
    margin-left: auto;
    font-size: 0.9rem;
    margin-top: -5px;
  }
`;

enum PaymentMethods {
  DEBIT = 'DEBIT',
  ACH = 'ACH',
}

type Props = SegmentProps & {
  application?: Application;
  onDebitEdit?: () => void;
};

const PaymentMethod: React.FC<Props> = ({ onDebitEdit, track, application }) => {
  const { t } = useTranslation();
  const [internalBankName, setInternalBankName] = useState<string>('');
  const routingNumber = application?.lease?.bank?.routingNumber;
  const accountNumberLastFour = application?.lease?.bank?.accountNumberLast4;
  const cardType = application?.lease?.card?.provider.toUpperCase();
  const paymentMethod = accountNumberLastFour ? PaymentMethods.ACH : PaymentMethods.DEBIT;
  const lastFour = paymentMethod === PaymentMethods.ACH ? accountNumberLastFour : application?.lease?.card?.lastFour;

  useEffect(() => {
    if (paymentMethod === PaymentMethods.ACH && routingNumber) {
      (async () => {
        const response = await loansService.getBankName(routingNumber);
        setInternalBankName(response?.data?.verifyRouting?.bankName?.toString() ?? '');
      })();
    }
    track('OTB LTO Method of Payment', { paymentMethod: paymentMethod });
  }, [routingNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const PaymentImgComponent = () => {
    if (paymentMethod === PaymentMethods.ACH) {
      return (
        <ImageStyle>
          <BankImage title={t(($) => $.componentPaymentMethod.paymentIcon)} />
        </ImageStyle>
      );
    } else {
      return (
        <ImageStyle>
          <CardImage title={t(($) => $.componentPaymentMethod.paymentIcon)} />
        </ImageStyle>
      );
    }
  };

  const ACHComponent = () => {
    return (
      <div>
        {/* <BankNameStyle></BankNameStyle> */}
        <PaymentImgComponent />
        {internalBankName} ...{lastFour}
      </div>
    );
  };

  const DebitComponent = () => {
    if (cardType === '') {
      return (
        <div>
          {t(($) => $.componentPaymentMethod.cardEndingIn)}${lastFour}
        </div>
      );
    } else {
      return (
        <div>
          <PaymentImgComponent />
          {cardType}...{lastFour}
        </div>
      );
    }
  };

  return (
    <PaymentMethodStyle>
      <PaymentHeader style={{ marginTop: -8 }}>{t(($) => $.componentPaymentMethod.methodOfPayment)}</PaymentHeader>
      <RowWrapper>
        <Label>
          <strong>{t(($) => $.componentPaymentMethod.recurringPaymentsDetails)}</strong>
        </Label>
        <Value>
          <div>
            {paymentMethod === PaymentMethods.DEBIT && <DebitComponent />}
            {paymentMethod === PaymentMethods.ACH && <ACHComponent />}
          </div>
        </Value>
        {paymentMethod === PaymentMethods.DEBIT && (
          <StyledLink>
            <Link
              onClick={() => {
                track('OTB Edit Debit Card Selected');
                if (!!onDebitEdit) {
                  onDebitEdit();
                }
              }}
              linkText={t(($) => $.componentPaymentMethod.edit)}
            />
          </StyledLink>
        )}
      </RowWrapper>
      {paymentMethod === PaymentMethods.ACH && (
        <p>
          <Trans
            i18nKey={($) => $.componentPaymentMethod.customerAuthorization}
            values={{ customerName: `${application?.customer?.firstName?.toUpperCase()} ${application?.customer?.lastName?.toUpperCase()}` }}
          >
            The customer,{' '}
            <strong>
              {application?.customer?.firstName?.toUpperCase()} {application?.customer?.lastName?.toUpperCase()}
            </strong>
            , authorizes Koalafi to validate and debit payments and fees from the provided bank account. The customer acknowledges that if approved, payment
            rights and obligations will be described in the Payment Method section in their lease agreement.
          </Trans>
        </p>
      )}
    </PaymentMethodStyle>
  );
};

export default PaymentMethod;
